import { supabase } from '@/integrations/supabase/client';

// ============= Win Rate Timeline Interfaces =============
export interface WinRateTimeSeriesPoint {
  timestamp: string;           // ISO timestamp of period start
  period_start: string;         // Display format
  period_end: string;          // Display format
  wins: number;
  losses: number;
  total_trades: number;
  win_rate: number;            // Period win rate
  cumulative_wins: number;
  cumulative_losses: number;
  cumulative_total: number;
  cumulative_win_rate: number; // Cumulative from start
}

export interface WinRateTimeSeriesOptions {
  timeRange: '24h' | '7d' | '30d' | '90d' | 'all';
  granularity: 'hour' | 'day' | 'week' | 'month';
}

export interface BotWinRateStats {
  bot_id: string;
  total_forwarded_exits: number;
  wins: number;
  losses: number;
  win_rate: number;
}

export interface ForwardedSignal {
  id: string;
  bot_id: string;
  signal_timestamp: string;
  action: string;
  instrument: string;
  win: number;
  response_status: number;
  response_ms: number | null;
  is_virtual_forward: boolean;
}

export interface Type2SystemStats {
  total_bots: number;
  active_bots: number;
  total_signals: number;
  forwarded_signals: number;
  skipped_signals: number;
  total_trades: number;
  wins: number;
  losses: number;
  system_win_rate: number;
  
  // All signals stats (before filter)
  all_signals_trades: number;
  all_signals_wins: number;
  all_signals_losses: number;
  all_signals_win_rate: number;
  
  // Improvement metrics
  win_rate_improvement_pts: number;
  win_rate_improvement_pct: number;
  
  forward_rate: number;
  skip_rate: number;
}

export interface Type2BotPerformance {
  bot_id: string;
  bot_name: string;
  total_signals: number;
  forwarded: number;
  skipped: number;
  trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  
  // All signals stats
  all_signals_trades: number;
  all_signals_wins: number;
  all_signals_losses: number;
  all_signals_win_rate: number;
  
  // Improvement metrics
  win_rate_improvement_pts: number;
  win_rate_improvement_pct: number;
  
  forward_rate: number;
  is_active: boolean;
  last_signal_at: string | null;
  
  // Permanent lock info
  active_lock_pattern_id?: string | null;
  is_permanent_locked?: boolean;
  lock_pattern?: string;
}

export async function fetchBotWinRateStats(
  botId: string,
  timeRange: 'today' | '7days' | '30days' | 'all' = 'all'
): Promise<BotWinRateStats> {
  console.log(`[BOT_STATS] Fetching for bot ${botId}, timeRange: ${timeRange}`);
  try {
    // Calculate time filter
    let timeFilter: Date | null = null;
    if (timeRange === 'today') {
      timeFilter = new Date();
      timeFilter.setHours(0, 0, 0, 0);
    } else if (timeRange === '7days') {
      timeFilter = new Date();
      timeFilter.setDate(timeFilter.getDate() - 7);
    } else if (timeRange === '30days') {
      timeFilter = new Date();
      timeFilter.setDate(timeFilter.getDate() - 30);
    }

    // Build base query for total exits
    let totalExitsQuery = supabase
      .from('conditional_skip_events')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200);
    
    if (timeFilter) {
      totalExitsQuery = totalExitsQuery.gte('created_at', timeFilter.toISOString());
    }
    const { count: totalExits } = await totalExitsQuery;
    
    // Build query for wins
    let winsQuery = supabase
      .from('conditional_skip_events')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200)
      .eq('win', 1);
    
    if (timeFilter) {
      winsQuery = winsQuery.gte('created_at', timeFilter.toISOString());
    }
    const { count: wins } = await winsQuery;
    
    // Build query for losses
    let lossesQuery = supabase
      .from('conditional_skip_events')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200)
      .eq('win', 0);
    
    if (timeFilter) {
      lossesQuery = lossesQuery.gte('created_at', timeFilter.toISOString());
    }
    const { count: losses } = await lossesQuery;
    
    const totalForwardedExits = totalExits || 0;
    const winCount = wins || 0;
    const lossCount = losses || 0;
    const winRate = totalForwardedExits > 0 ? (winCount / totalForwardedExits) * 100 : 0;
    
    return {
      bot_id: botId,
      total_forwarded_exits: totalForwardedExits,
      wins: winCount,
      losses: lossCount,
      win_rate: winRate
    };
  } catch (error) {
    console.error('Error fetching bot win rate stats:', error);
    return {
      bot_id: botId,
      total_forwarded_exits: 0,
      wins: 0,
      losses: 0,
      win_rate: 0
    };
  }
}

export async function fetchAllBotsWinRateStats(
  timeRange: 'today' | '7days' | '30days' | 'all' = 'all'
): Promise<Map<string, BotWinRateStats>> {
  try {
    // Calculate time filter
    let timeFilterStart: string | null = null;
    if (timeRange === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      timeFilterStart = today.toISOString();
    } else if (timeRange === '7days') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      timeFilterStart = d.toISOString();
    } else if (timeRange === '30days') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      timeFilterStart = d.toISOString();
    }
    // For 'all', timeFilterStart remains null

    console.log(`[WIN_RATE_STATS] Calling RPC with timeRange: ${timeRange}, timeFilterStart: ${timeFilterStart}`);

    // Call the database RPC function for efficient aggregation
    const { data, error } = await supabase.rpc('get_all_bots_win_rate_stats', {
      time_filter_start: timeFilterStart
    });

    if (error) {
      console.error('[WIN_RATE_STATS] RPC Error:', error);
      throw error;
    }

    console.log(`[WIN_RATE_STATS] RPC returned ${data?.length || 0} bots`);
    
    // Log sample data for debugging
    if (data && data.length > 0) {
      console.log('[WIN_RATE_STATS] Sample data (first 5):', data.slice(0, 5));
      
      // Log specific bots for debugging
      const xauusdBot = data.find((d: any) => d.bot_id === '38b6fd89-0f0f-436a-839f-7db15f94e22f');
      const cadjpyBot = data.find((d: any) => d.bot_id === '8a1f1f3c-36cf-49d0-a62c-5e356fd8cc98');
      if (xauusdBot) console.log('[WIN_RATE_STATS] XAUUSD bot:', xauusdBot);
      if (cadjpyBot) console.log('[WIN_RATE_STATS] CADJPY bot:', cadjpyBot);
    }

    // Convert to Map
    const statsMap = new Map<string, BotWinRateStats>();
    (data || []).forEach((row: any) => {
      statsMap.set(row.bot_id, {
        bot_id: row.bot_id,
        total_forwarded_exits: Number(row.total_forwarded_exits),
        wins: Number(row.wins),
        losses: Number(row.losses),
        win_rate: Number(row.win_rate)
      });
    });

    console.log(`[WIN_RATE_STATS] Mapped ${statsMap.size} bots to Map`);

    return statsMap;
  } catch (error) {
    console.error('[WIN_RATE_STATS] Error fetching all bots win rate stats:', error);
    return new Map();
  }
}

// ============= CSV Export Interfaces =============
export interface BotWinRateAllRanges {
  bot_id: string;
  bot_name: string;
  wr_total: number;
  trades_total: number;
  wr_30d: number;
  trades_30d: number;
  wr_7d: number;
  trades_7d: number;
  wr_today: number;
  trades_today: number;
}

// ============= Fetch All Time Ranges for CSV Export =============
export async function fetchAllBotsWinRateAllRanges(): Promise<BotWinRateAllRanges[]> {
  try {
    // 1. Get all Type 2 bots with names
    const { data: type2Bots, error: botsError } = await supabase
      .from('conditional_skip_settings')
      .select('bot_id, bots(name)')
      .eq('skip_type', 'FLEXIBLE_PATTERN');
    
    if (botsError) throw botsError;
    if (!type2Bots || type2Bots.length === 0) return [];
    
    // 2. Calculate time filters
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // 3. Fetch all 4 time ranges in parallel using RPC
    const [statsAll, stats30d, stats7d, statsToday] = await Promise.all([
      supabase.rpc('get_all_bots_win_rate_stats', { time_filter_start: null }),
      supabase.rpc('get_all_bots_win_rate_stats', { time_filter_start: thirtyDaysAgo.toISOString() }),
      supabase.rpc('get_all_bots_win_rate_stats', { time_filter_start: sevenDaysAgo.toISOString() }),
      supabase.rpc('get_all_bots_win_rate_stats', { time_filter_start: today.toISOString() }),
    ]);
    
    // 4. Convert to Maps for easy lookup
    const mapAll = new Map((statsAll.data || []).map((d: any) => [d.bot_id, d]));
    const map30d = new Map((stats30d.data || []).map((d: any) => [d.bot_id, d]));
    const map7d = new Map((stats7d.data || []).map((d: any) => [d.bot_id, d]));
    const mapToday = new Map((statsToday.data || []).map((d: any) => [d.bot_id, d]));
    
    // 5. Merge results for each bot
    return type2Bots.map(bot => {
      const botId = bot.bot_id;
      const botName = (bot.bots as any)?.name || 'Unknown';
      
      const all = mapAll.get(botId) as any;
      const d30 = map30d.get(botId) as any;
      const d7 = map7d.get(botId) as any;
      const td = mapToday.get(botId) as any;
      
      return {
        bot_id: botId,
        bot_name: botName,
        wr_total: Number(all?.win_rate) || 0,
        trades_total: Number(all?.total_forwarded_exits) || 0,
        wr_30d: Number(d30?.win_rate) || 0,
        trades_30d: Number(d30?.total_forwarded_exits) || 0,
        wr_7d: Number(d7?.win_rate) || 0,
        trades_7d: Number(d7?.total_forwarded_exits) || 0,
        wr_today: Number(td?.win_rate) || 0,
        trades_today: Number(td?.total_forwarded_exits) || 0,
      };
    });
  } catch (error) {
    console.error('[CSV_EXPORT] Error fetching all bots win rate:', error);
    throw error;
  }
}

// ============= Export to CSV =============
export function exportBotsWinRateToCSV(data: BotWinRateAllRanges[]): void {
  // CSV Header
  const headers = [
    'Bot Name',
    '%WR (Total)',
    'Trade (Total)',
    '%WR (30 days)',
    'Trade (30 days)',
    '%WR (7 days)',
    'Trade (7 days)',
    '%WR (Today)',
    'Trade (Today)'
  ];
  
  // Sort by total trades descending
  const sortedData = [...data].sort((a, b) => b.trades_total - a.trades_total);
  
  // CSV Rows
  const rows = sortedData.map(bot => [
    bot.bot_name,
    bot.wr_total.toFixed(2),
    bot.trades_total.toString(),
    bot.wr_30d.toFixed(2),
    bot.trades_30d.toString(),
    bot.wr_7d.toFixed(2),
    bot.trades_7d.toString(),
    bot.wr_today.toFixed(2),
    bot.trades_today.toString()
  ]);
  
  // Build CSV content with proper escaping
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  
  // Create and trigger download
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `type2_bot_winrate_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function fetchBotExitSignals(
  botId: string,
  page: number = 1,
  pageSize: number = 20,
  winFilter: 'all' | 'win' | 'loss' = 'all',
  searchQuery: string = '',
  forwardTypeFilter: 'all' | 'real' | 'virtual' = 'all'
): Promise<{
  signals: ForwardedSignal[];
  total: number;
  page: number;
  pageSize: number;
}> {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Build query
    let query = supabase
      .from('conditional_skip_events')
      .select('id, bot_id, signal_timestamp, action, instrument, win, response_status, response_ms, is_virtual_forward', { count: 'exact' })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200);
    
    // Apply win filter
    if (winFilter === 'win') {
      query = query.eq('win', 1);
    } else if (winFilter === 'loss') {
      query = query.eq('win', 0);
    }
    
    // Apply forward type filter
    if (forwardTypeFilter === 'real') {
      query = query.eq('is_virtual_forward', false);
    } else if (forwardTypeFilter === 'virtual') {
      query = query.eq('is_virtual_forward', true);
    }
    
    // Apply search filter
    if (searchQuery) {
      query = query.ilike('instrument', `%${searchQuery}%`);
    }
    
    // Apply ordering and pagination
    const { data: signals, error, count } = await query
      .order('signal_timestamp', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    return {
      signals: signals as ForwardedSignal[] || [],
      total: count || 0,
      page,
      pageSize
    };
  } catch (error) {
    console.error('Error fetching bot exit signals:', error);
    return {
      signals: [],
      total: 0,
      page,
      pageSize
    };
  }
}

export async function fetchType2SystemStats(timeRange: 'today' | '7days' | '30days' | 'all' = 'all'): Promise<Type2SystemStats> {
  try {
    // Get time filter
    let timeFilterStart: string | null = null;
    if (timeRange === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      timeFilterStart = today.toISOString();
    } else if (timeRange === '7days') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      timeFilterStart = d.toISOString();
    } else if (timeRange === '30days') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      timeFilterStart = d.toISOString();
    }

    // Get all Type 2 bots
    const { data: type2Settings } = await supabase
      .from('conditional_skip_settings')
      .select('bot_id')
      .eq('skip_type', 'FLEXIBLE_PATTERN');

    const botIds = type2Settings?.map(s => s.bot_id) || [];
    const total_bots = botIds.length;

    if (botIds.length === 0) {
      return {
        total_bots: 0,
        active_bots: 0,
        total_signals: 0,
        forwarded_signals: 0,
        skipped_signals: 0,
        total_trades: 0,
        wins: 0,
        losses: 0,
        system_win_rate: 0,
        all_signals_trades: 0,
        all_signals_wins: 0,
        all_signals_losses: 0,
        all_signals_win_rate: 0,
        win_rate_improvement_pts: 0,
        win_rate_improvement_pct: 0,
        forward_rate: 0,
        skip_rate: 0
      };
    }

    // Get current states to count active bots AND aggregated performance data in parallel
    const [statesResult, performanceResult] = await Promise.all([
      supabase
        .from('bot_current_condition_skip')
        .select('bot_id')
        .in('bot_id', botIds),
      supabase.rpc('get_type2_bot_performance', { time_filter_start: timeFilterStart })
    ]);

    const active_bots = statesResult.data?.length || 0;
    
    if (performanceResult.error) {
      console.error('Error fetching performance data:', performanceResult.error);
      throw performanceResult.error;
    }

    // Filter performance data to only Type 2 bots and aggregate
    const type2Performance = (performanceResult.data || []).filter((p: any) => 
      botIds.includes(p.bot_id)
    );

    // Aggregate system stats from RPC results
    let total_signals = 0;
    let forwarded_signals = 0;
    let skipped_signals = 0;
    let total_trades = 0;
    let wins = 0;
    let losses = 0;
    let all_signals_trades = 0;
    let all_signals_wins = 0;
    let all_signals_losses = 0;

    type2Performance.forEach((p: any) => {
      total_signals += Number(p.total_signals) || 0;
      forwarded_signals += Number(p.forwarded) || 0;
      skipped_signals += Number(p.skipped) || 0;
      total_trades += Number(p.trades) || 0;
      wins += Number(p.wins) || 0;
      losses += Number(p.losses) || 0;
      all_signals_trades += Number(p.all_signals_trades) || 0;
      all_signals_wins += Number(p.all_signals_wins) || 0;
      all_signals_losses += Number(p.all_signals_losses) || 0;
    });

    const system_win_rate = total_trades > 0 ? (wins / total_trades) * 100 : 0;
    const all_signals_win_rate = all_signals_trades > 0 
      ? (all_signals_wins / all_signals_trades) * 100 
      : 0;

    // Calculate improvement
    const win_rate_improvement_pts = system_win_rate - all_signals_win_rate;
    const win_rate_improvement_pct = all_signals_win_rate > 0
      ? ((system_win_rate - all_signals_win_rate) / all_signals_win_rate) * 100
      : 0;

    const forward_rate = total_signals > 0 ? (forwarded_signals / total_signals) * 100 : 0;
    const skip_rate = total_signals > 0 ? (skipped_signals / total_signals) * 100 : 0;

    return {
      total_bots,
      active_bots,
      total_signals,
      forwarded_signals,
      skipped_signals,
      total_trades,
      wins,
      losses,
      system_win_rate,
      all_signals_trades,
      all_signals_wins,
      all_signals_losses,
      all_signals_win_rate,
      win_rate_improvement_pts,
      win_rate_improvement_pct,
      forward_rate,
      skip_rate
    };
  } catch (error) {
    console.error('Error fetching Type 2 system stats:', error);
    return {
      total_bots: 0,
      active_bots: 0,
      total_signals: 0,
      forwarded_signals: 0,
      skipped_signals: 0,
      total_trades: 0,
      wins: 0,
      losses: 0,
      system_win_rate: 0,
      all_signals_trades: 0,
      all_signals_wins: 0,
      all_signals_losses: 0,
      all_signals_win_rate: 0,
      win_rate_improvement_pts: 0,
      win_rate_improvement_pct: 0,
      forward_rate: 0,
      skip_rate: 0
    };
  }
}

export async function fetchType2BotPerformance(timeRange: 'today' | '7days' | '30days' | 'all' = 'all'): Promise<Type2BotPerformance[]> {
  try {
    // Get time filter
    let timeFilterStart: string | null = null;
    if (timeRange === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      timeFilterStart = today.toISOString();
    } else if (timeRange === '7days') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      timeFilterStart = d.toISOString();
    } else if (timeRange === '30days') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      timeFilterStart = d.toISOString();
    }

    // Get all Type 2 bots with names, performance data, and state info in parallel
    const [settingsResult, performanceResult, statesResult] = await Promise.all([
      supabase
        .from('conditional_skip_settings')
        .select('bot_id, bots(name)')
        .eq('skip_type', 'FLEXIBLE_PATTERN'),
      supabase.rpc('get_type2_bot_performance', { time_filter_start: timeFilterStart }),
      supabase
        .from('bot_current_condition_skip')
        .select(`
          bot_id,
          active_lock_pattern_id,
          flexible_skip_patterns(is_permanent, lock_pattern)
        `)
    ]);

    if (!settingsResult.data || settingsResult.data.length === 0) {
      return [];
    }

    if (performanceResult.error) {
      console.error('Error fetching performance from RPC:', performanceResult.error);
      throw performanceResult.error;
    }

    // Create maps for quick lookup
    const performanceMap = new Map(
      (performanceResult.data || []).map((p: any) => [p.bot_id, p])
    );
    const stateMap = new Map(
      (statesResult.data || []).map((s: any) => [s.bot_id, s])
    );

    // Build performance objects
    const performances: Type2BotPerformance[] = settingsResult.data.map((setting) => {
      const botId = setting.bot_id;
      const botName = (setting as any).bots?.name || 'Unknown';
      const perf = performanceMap.get(botId);
      const state = stateMap.get(botId);

      const total_signals = Number(perf?.total_signals) || 0;
      const forwarded = Number(perf?.forwarded) || 0;
      const skipped = Number(perf?.skipped) || 0;
      const total_trades = Number(perf?.trades) || 0;
      const wins = Number(perf?.wins) || 0;
      const losses = Number(perf?.losses) || 0;
      const win_rate = total_trades > 0 ? (wins / total_trades) * 100 : 0;
      const forward_rate = total_signals > 0 ? (forwarded / total_signals) * 100 : 0;

      // All signals stats
      const all_signals_trades = Number(perf?.all_signals_trades) || 0;
      const all_signals_wins = Number(perf?.all_signals_wins) || 0;
      const all_signals_losses = Number(perf?.all_signals_losses) || 0;
      const all_signals_win_rate = all_signals_trades > 0 
        ? (all_signals_wins / all_signals_trades) * 100 
        : 0;

      // Calculate improvement
      const win_rate_improvement_pts = win_rate - all_signals_win_rate;
      const win_rate_improvement_pct = all_signals_win_rate > 0
        ? ((win_rate - all_signals_win_rate) / all_signals_win_rate) * 100
        : 0;

      return {
        bot_id: botId,
        bot_name: botName,
        total_signals,
        forwarded,
        skipped,
        trades: total_trades,
        wins,
        losses,
        win_rate,
        all_signals_trades,
        all_signals_wins,
        all_signals_losses,
        all_signals_win_rate,
        win_rate_improvement_pts,
        win_rate_improvement_pct,
        forward_rate,
        is_active: !!state,
        last_signal_at: perf?.last_signal_at || null,
        active_lock_pattern_id: state?.active_lock_pattern_id,
        is_permanent_locked: (state as any)?.flexible_skip_patterns?.is_permanent || false,
        lock_pattern: (state as any)?.flexible_skip_patterns?.lock_pattern
      };
    });

    return performances.sort((a, b) => b.total_signals - a.total_signals);
  } catch (error) {
    console.error('Error fetching Type 2 bot performance:', error);
    return [];
  }
}

// ============= Win Rate Timeline Service =============

/**
 * Fetches win rate time series data for a bot
 * Uses WR (Filtered) logic: only counts response_status = 200
 */
export async function fetchBotWinRateTimeSeries(
  botId: string,
  options: WinRateTimeSeriesOptions
): Promise<WinRateTimeSeriesPoint[]> {
  try {
    // Step 1: Calculate time range
    let startDate: Date | null = null;
    const now = new Date();
    
    switch (options.timeRange) {
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
    }
    
    // Step 2: Fetch filtered exit signals (matching Monitor WR logic)
    let query = supabase
      .from('conditional_skip_events')
      .select('signal_timestamp, win')
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200)  // ✅ WR (Filtered) logic!
      .not('win', 'is', null)
      .order('signal_timestamp', { ascending: true })
      .limit(10000);
    
    if (startDate) {
      query = query.gte('signal_timestamp', startDate.toISOString());
    }
    
    const { data: exitSignals, error } = await query;
    
    if (error) {
      console.error('Error fetching exit signals for timeline:', error);
      throw error;
    }
    
    if (!exitSignals || exitSignals.length === 0) {
      return [];
    }
    
    // Step 3: Group by time intervals
    const grouped = groupByTimeInterval(exitSignals, options.granularity);
    
    // Step 4: Calculate cumulative stats
    let cumulativeWins = 0;
    let cumulativeLosses = 0;
    
    return grouped.map(group => {
      cumulativeWins += group.wins;
      cumulativeLosses += group.losses;
      const cumulativeTotal = cumulativeWins + cumulativeLosses;
      
      return {
        timestamp: group.period_start,
        period_start: formatPeriod(group.period_start, options.granularity),
        period_end: formatPeriod(group.period_end, options.granularity),
        wins: group.wins,
        losses: group.losses,
        total_trades: group.total,
        win_rate: group.total > 0 ? (group.wins / group.total) * 100 : 0,
        cumulative_wins: cumulativeWins,
        cumulative_losses: cumulativeLosses,
        cumulative_total: cumulativeTotal,
        cumulative_win_rate: cumulativeTotal > 0 
          ? (cumulativeWins / cumulativeTotal) * 100 
          : 0
      };
    });
  } catch (error) {
    console.error('Error fetching bot win rate time series:', error);
    throw error;
  }
}

// Helper: Group signals by time interval
function groupByTimeInterval(signals: any[], granularity: string) {
  const groups: Record<string, any> = {};
  
  signals.forEach(signal => {
    const timestamp = new Date(signal.signal_timestamp);
    let periodKey: string;
    
    switch (granularity) {
      case 'hour':
        timestamp.setMinutes(0, 0, 0);
        periodKey = timestamp.toISOString();
        break;
      case 'day':
        timestamp.setHours(0, 0, 0, 0);
        periodKey = timestamp.toISOString();
        break;
      case 'week':
        // Start of week (Monday)
        const day = timestamp.getDay();
        const diff = timestamp.getDate() - day + (day === 0 ? -6 : 1);
        timestamp.setDate(diff);
        timestamp.setHours(0, 0, 0, 0);
        periodKey = timestamp.toISOString();
        break;
      case 'month':
        timestamp.setDate(1);
        timestamp.setHours(0, 0, 0, 0);
        periodKey = timestamp.toISOString();
        break;
      default:
        periodKey = timestamp.toISOString();
    }
    
    if (!groups[periodKey]) {
      groups[periodKey] = {
        period_start: periodKey,
        wins: 0,
        losses: 0,
        total: 0
      };
    }
    
    groups[periodKey].total++;
    if (signal.win === 1) {
      groups[periodKey].wins++;
    } else if (signal.win === 0) {
      groups[periodKey].losses++;
    }
  });
  
  // Calculate period_end for each group and sort
  return Object.values(groups)
    .map(group => {
      const start = new Date(group.period_start);
      let end = new Date(start);
      
      switch (granularity) {
        case 'hour':
          end.setHours(end.getHours() + 1);
          break;
        case 'day':
          end.setDate(end.getDate() + 1);
          break;
        case 'week':
          end.setDate(end.getDate() + 7);
          break;
        case 'month':
          end.setMonth(end.getMonth() + 1);
          break;
      }
      
      return {
        ...group,
        period_end: end.toISOString()
      };
    })
    .sort((a, b) => new Date(a.period_start).getTime() - new Date(b.period_start).getTime());
}

// Helper: Format period for display
function formatPeriod(isoString: string, granularity: string): string {
  const date = new Date(isoString);
  
  switch (granularity) {
    case 'hour':
      return date.toLocaleString('vi-VN', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit',
        minute: '2-digit'
      });
    case 'day':
      return date.toLocaleString('vi-VN', { 
        month: 'short', 
        day: 'numeric' 
      });
    case 'week': {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 6);
      return `${date.getDate()}-${endDate.getDate()} ${date.toLocaleString('vi-VN', { month: 'short' })}`;
    }
    case 'month':
      return date.toLocaleString('vi-VN', { 
        month: 'short', 
        year: 'numeric' 
      });
    default:
      return date.toLocaleDateString('vi-VN');
  }
}

